<template>
  <!--
    Fase 1 do fluxo de auditoria (spec 2026-09-11, §5.3) — ocupa o lugar da grade do espelho
    quando o prefill volta vazio COM motivo. Neutro, nunca vermelho: não é erro do operador
    nem do sistema, e a grade clássica de digitação continua logo abaixo. Sem motivo (backend
    anterior à fase 1) o componente não é renderizado e a tela segue silenciosa, como antes.
  -->
  <div
    v-if="emptyState"
    class="mb-3 flex items-start gap-2 rounded-lg border border-gray-300 bg-gray-50 p-3 text-sm text-textPrimary"
    data-testid="prefill-empty-state"
    :data-reason="emptyState.reason"
  >
    <svg
      class="mt-0.5 h-4 w-4 shrink-0 text-neutral50"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path :d="mdiInformationOutline" fill="currentColor" />
    </svg>
    <div class="min-w-0">
      <p data-testid="prefill-empty-state-text">
        <template v-for="(segment, index) in emptyState.segments" :key="index">
          <strong v-if="segment.strong">{{ segment.text }}</strong>
          <template v-else>{{ segment.text }}</template>
        </template>
      </p>
      <!--
        Atalho para conferir a escala do dia: o Dashboard já parseia `startDate` + `schedules`
        (mesmo formato do deep-link de "Abrir plantão" do Pós-plantão).
      -->
      <router-link
        v-if="emptyState.showScheduleLink && scheduleLink"
        :to="scheduleLink"
        class="mt-1 inline-block font-bold text-primary underline"
        data-testid="prefill-empty-state-schedule-link"
      >
        Ver escala do dia
      </router-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiInformationOutline } from '@mdi/js'
import type { RouteLocationRaw } from 'vue-router'
import PATHS from '@/router/paths'
import type { DSAttendancePrefillEmptyReason } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { buildPrefillEmptyState } from '../utils/prefill-empty-state'

const props = withDefaults(
  defineProps<{
    /** Motivo devolvido pelo prefill; ausente ⇒ nada é renderizado. */
    emptyReason?: DSAttendancePrefillEmptyReason | null
    scheduleName?: string | null
    /** Data de referência já formatada para exibição (dd/MM/yyyy). */
    date?: string | null
    shifts?: string | null
    /** Recorrência da regra da política; sem ela o parêntese some do texto de período. */
    recurrence?: string | null
    /** Data ISO (yyyy-MM-dd) e escala alimentam o link "Ver escala do dia". */
    referenceDate?: string | null
    scheduleId?: number | string | null
  }>(),
  {
    emptyReason: null,
    scheduleName: null,
    date: null,
    shifts: null,
    recurrence: null,
    referenceDate: null,
    scheduleId: null
  }
)

const emptyState = computed(() =>
  buildPrefillEmptyState({
    emptyReason: props.emptyReason,
    scheduleName: props.scheduleName,
    date: props.date,
    shifts: props.shifts,
    recurrence: props.recurrence
  })
)

/** Sem data ou sem escala não há para onde levar — o link simplesmente não aparece. */
const scheduleLink = computed<RouteLocationRaw | null>(() => {
  if (!props.referenceDate || props.scheduleId === null || props.scheduleId === undefined) {
    return null
  }
  return {
    path: PATHS.DASHBOARD,
    query: { startDate: props.referenceDate, schedules: String(props.scheduleId) }
  }
})
</script>
